// Anatomia da tela "Uso da API" (/messages-api) no padrão Aeros Dark —
// mockup "Tela Aeros Dark - API de Mensagens".
//
// O painel de código e os inputs do card "Testar envio" ficam escuros nos
// DOIS temas de propósito: o exemplo de requisição precisa ler-se igual em
// qualquer configuração, e o mockup desenha os campos como terminal.
// O resto (cards, textos, bordas) segue os tokens do tema.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Paleta fixa do painel de código / inputs "terminal".
export const CODE = Object.freeze({
  bg: "#0E1119",
  border: "rgba(255,255,255,0.08)",
  borderInput: "rgba(255,255,255,0.1)",
  text: "#F1F2F6",
  key: "#22D3EE",
  muted: "#5B6376",
  label: "#9CA3B8",
});

const useApiPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const borderCard = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";

  return {
    // ---- header 60px: título + subtítulo, ações à direita ----
    pageSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
      marginTop: 1,
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 99,
      padding: "5px 12px",
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.1),
    },
    statusDot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      backgroundColor: AEROS.success,
      flex: "none",
    },

    // ---- corpo ----
    body: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: "24px 12px",
      display: "flex",
      flexDirection: "column",
      gap: 26,
      ...theme.scrollbarStylesSoft,
    },

    // ---- faixa de introdução: card do endpoint + KPIs ----
    introGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr)",
      gap: 14,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 14,
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    introText: {
      fontSize: 13,
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      "& b": { fontWeight: 700 },
    },
    endpointRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: isLight ? theme.palette.aeros.surface2 : CODE.bg,
      border: `1px solid ${isLight ? theme.palette.divider : CODE.border}`,
      borderRadius: 10,
      padding: "9px 12px",
      fontFamily: MONO,
      fontSize: 12.5,
      minWidth: 0,
    },
    methodBadge: {
      backgroundColor: alpha(AEROS.success, 0.15),
      color: stateText(theme, AEROS.success, "success"),
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: "0.08em",
      borderRadius: 5,
      padding: "2px 8px",
      fontFamily: "inherit",
      flex: "none",
    },
    endpointText: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: isLight ? theme.palette.text.primary : CODE.text,
    },
    endpointPath: { color: AEROS.cyan },
    iconGhost: {
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "&:hover": { color: AEROS.cyan, backgroundColor: "transparent" },
    },

    // ---- chips do formato do número ----
    numberRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    numberChips: { display: "flex", alignItems: "center", gap: 4 },
    numberChip: {
      fontFamily: MONO,
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 6,
      padding: "3px 8px",
    },
    chipCountry: {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      color: stateText(theme, "#C08BFF", "purple"),
    },
    chipArea: {
      backgroundColor: alpha(AEROS.cyan, 0.14),
      color: stateText(theme, AEROS.cyan, "info"),
    },
    chipNumber: {
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "rgba(255,255,255,0.08)",
      color: theme.palette.text.primary,
    },
    numberLegend: { color: theme.palette.aeros.textTertiary },

    // ---- KPIs ----
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
      gap: 10,
      alignContent: "start",
    },
    kpiCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 14,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minHeight: 62,
    },
    kpiValue: {
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.1,
      color: theme.palette.text.primary,
    },
    kpiLabel: { fontSize: 11.5, color: theme.palette.text.secondary, marginTop: 3 },

    // ---- seções (Método 1/2, 2/2, Referência) ----
    section: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      paddingTop: 22,
      borderTop: `1px solid ${borderSoft}`,
    },
    eyebrow: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: stateText(theme, "#C08BFF", "purple"),
    },
    sectionTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      marginTop: 4,
      color: theme.palette.text.primary,
    },
    sectionIntro: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      marginTop: 2,
      maxWidth: 720,
      lineHeight: 1.5,
    },
    inlineCode: { fontFamily: MONO, color: theme.palette.text.primary },
    methodGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 14,
      alignItems: "start",
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    // Seção SMS: só painéis de código, um abaixo do outro — o de envio é
    // largo (JSON + resposta) e lado a lado cortaria tudo em ~520px.
    smsGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr)",
      gap: 14,
    },

    // ---- painel de código (escuro nos dois temas) ----
    codePanel: {
      backgroundColor: CODE.bg,
      border: `1px solid ${CODE.border}`,
      borderRadius: 12,
      overflow: "hidden",
      fontFamily: MONO,
      fontSize: 12.5,
      lineHeight: 1.7,
    },
    codeHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px 8px 14px",
      borderBottom: `1px solid ${CODE.border}`,
      minWidth: 0,
    },
    codeHeaderBadge: {
      backgroundColor: alpha(AEROS.success, 0.15),
      color: AEROS.success,
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: "0.08em",
      borderRadius: 5,
      padding: "2px 8px",
      fontFamily: theme.typography.fontFamily,
      flex: "none",
    },
    codeHeaderUrl: {
      flex: 1,
      minWidth: 0,
      color: CODE.text,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    codeCopy: {
      padding: 4,
      color: CODE.muted,
      flex: "none",
      "&:hover": { color: CODE.text, backgroundColor: "transparent" },
    },
    tabsRow: { display: "flex", gap: 4, padding: "8px 14px 0", flexWrap: "wrap" },
    tab: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontFamily: theme.typography.fontFamily,
      fontSize: 11.5,
      fontWeight: 600,
      color: CODE.muted,
      borderRadius: 6,
      padding: "3px 10px",
      "&:hover": { color: CODE.text },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
    },
    tabActive: { backgroundColor: "rgba(255,255,255,0.08)", color: CODE.text },
    codeBody: {
      padding: "14px 16px",
      overflowX: "auto",
      color: CODE.text,
      ...theme.scrollbarStylesSoft,
    },
    codeLine: { whiteSpace: "pre" },
    codeKey: { color: CODE.key },
    codeComment: { color: CODE.muted },

    // ---- card "Testar envio" ----
    formHead: { display: "flex", alignItems: "center", gap: 10 },
    formTitle: {
      fontSize: 13.5,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    formHint: {
      marginLeft: "auto",
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
    },
    officialPill: {
      marginLeft: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      color: stateText(theme, AEROS.cyan, "info"),
      backgroundColor: alpha(AEROS.cyan, 0.12),
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    required: { color: AEROS.error },
    optional: { color: theme.palette.aeros.textTertiary, fontWeight: 500 },
    fieldPair: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 12,
    },
    helper: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 5,
    },

    // Input "terminal": mesma casca do mockup para text, select e textarea.
    input: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: isLight ? theme.palette.aeros.surface2 : CODE.bg,
        borderRadius: 10,
        fontSize: 13,
        color: theme.palette.text.primary,
        "& fieldset": {
          borderColor: isLight ? theme.palette.aeros.borderStrong : CODE.borderInput,
        },
        "&:hover fieldset": { borderColor: alpha(AEROS.cyan, 0.5) },
        "&.Mui-focused fieldset": { borderColor: AEROS.cyan, borderWidth: 1 },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}` },
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiSelect-select": { padding: "9px 12px" },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        fontSize: 17,
        color: theme.palette.aeros.textTertiary,
      },
    },
    inputMono: { "& .MuiOutlinedInput-input": { fontFamily: MONO, fontSize: 12.5 } },
    inputNumeric: { "& .MuiOutlinedInput-input": { fontVariantNumeric: "tabular-nums" } },
    connDot: { width: 8, height: 8, borderRadius: "50%", flex: "none" },
    connOption: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    channelTag: {
      fontSize: 10,
      fontWeight: 700,
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
      borderRadius: 5,
      padding: "1px 6px",
    },
    channelTagOfficial: {
      color: stateText(theme, AEROS.cyan, "info"),
      borderColor: alpha(AEROS.cyan, 0.4),
    },

    // ---- bloco roxo "Preencher template" ----
    templateBlock: {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    templateBlockTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: stateText(theme, "#C08BFF", "purple"),
    },
    // Campo Mensagem quando um template assume o corpo.
    bodyDisabled: {
      backgroundColor: alpha(isLight ? theme.palette.aeros.surface2 : CODE.bg, 0.5),
      border: `1px dashed ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "9px 12px",
      fontSize: 13,
      color: theme.palette.aeros.textTertiary,
      minHeight: 40,
      display: "flex",
      alignItems: "center",
      gap: 6,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },

    // ---- arquivo escolhido (método 2) ----
    fileCard: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: isLight ? theme.palette.aeros.surface2 : CODE.bg,
      border: `1px solid ${borderCard}`,
      borderRadius: 10,
      padding: "9px 12px",
      minWidth: 0,
    },
    fileTile: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      backgroundColor: alpha(AEROS.error, 0.14),
      color: AEROS.error,
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    fileTileImage: {
      backgroundColor: alpha(AEROS.cyan, 0.14),
      color: stateText(theme, AEROS.cyan, "info"),
    },
    fileTileEmpty: {
      backgroundColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.06)",
      color: theme.palette.aeros.textTertiary,
    },
    fileName: {
      fontSize: 12.5,
      fontWeight: 600,
      fontFamily: MONO,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: theme.palette.text.primary,
    },
    fileMeta: { fontSize: 11, color: theme.palette.text.secondary },
    fileButton: {
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "none",
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
      borderRadius: 8,
      padding: "4px 10px",
      flex: "none",
      "&:hover": { borderColor: alpha(AEROS.cyan, 0.5), color: AEROS.cyan },
    },

    // ---- toggles (assinar / encerrar ticket) ----
    toggleRow: { display: "flex", gap: 16, flexWrap: "wrap" },
    toggleLabel: {
      margin: 0,
      gap: 8,
      "& .MuiFormControlLabel-label": {
        fontSize: 12.5,
        color: theme.palette.text.secondary,
      },
      "&.Mui-checked-label .MuiFormControlLabel-label": {
        color: theme.palette.text.primary,
      },
    },
    switchRoot: {
      width: 32,
      height: 18,
      padding: 0,
      display: "flex",
      overflow: "visible",
      "& .MuiSwitch-switchBase": {
        padding: 2,
        color: theme.palette.aeros.textTertiary,
        "&.Mui-checked": {
          transform: "translateX(14px)",
          color: "#07080D",
          "& + .MuiSwitch-track": {
            opacity: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
          },
        },
      },
      "& .MuiSwitch-thumb": { width: 14, height: 14, boxShadow: "none" },
      "& .MuiSwitch-track": {
        borderRadius: 99,
        opacity: 1,
        backgroundColor: isLight
          ? theme.palette.aeros.borderStrong
          : "rgba(255,255,255,0.12)",
      },
    },

    // ---- rodapé do card de teste ----
    formFooter: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      paddingTop: 6,
      flexWrap: "wrap",
    },
    lastTest: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    lastTestOk: { color: stateText(theme, AEROS.success, "success") },
    lastTestFail: { color: stateText(theme, AEROS.error, "error") },
    sendButton: {
      marginLeft: "auto",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "9px 18px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
      "&:hover": { boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}` },
      "&.Mui-disabled": { opacity: 0.5, color: "#07080D" },
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 16 },
    },

    // ---- cards de código de resposta ----
    codesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      gap: 10,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
    },
    codeCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
    },
    codeCardBadge: {
      fontFamily: MONO,
      fontSize: 14,
      fontWeight: 700,
      borderRadius: 6,
      padding: "3px 8px",
      flex: "none",
    },
    codeCardTitle: { fontSize: 12.5, fontWeight: 600, color: theme.palette.text.primary },
    codeCardDesc: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.45,
      marginTop: 2,
    },
  };
});

export default useApiPageStyles;
